import React, { useEffect, useState, useRef } from 'react';
import useNewsBulletinsStore from '../../stores/newsBulletinsStore';
import { useAuthStore } from '../../stores/authStore';
import { useDialogStore } from '../../stores/dialogStore';
import { useGalleriesStore } from '../../stores/galleriesStore';
import { useLayoutsStore } from '../../stores/layoutsStore';
import RichTextEditorExtended from '../RichTextEditorExtended';
import GalleryRenderer from '../GalleryRenderer';
import ProtectedImage from '../ProtectedImage';
import ProtectedVideo from '../ProtectedVideo';

const NewsBulletinWidget = ({ context_id, profileId }) => {
  const { accessToken, profile, user } = useAuthStore();
  const { alert, confirm } = useDialogStore();
  const isAdmin = user?.is_admin === 1;
  const {
    bulletins,
    loading,
    hasMore,
    fetchBulletins,
    createBulletin,
    updateBulletin,
    deleteBulletin,
    togglePin,
    fetchCalendarData,
    calendarDates,
    searchBulletins,
    searchResults,
    clearSearch,
    reset,
    jumpToDate,
    jumpToBulletin,
    resetToLatest,
    selectedDate,
    selectedBulletinId,
  } = useNewsBulletinsStore();
  const {
    createGallery,
    deleteGallery,
    fetchGallery,
    addMedia,
    deleteMedia,
    updateGallery,
    reorderMedia
  } = useGalleriesStore();
  const { mediaLayouts, fetchMediaLayouts } = useLayoutsStore();

  const [contextId, setContextId] = useState(context_id || null);
  const [isCreating, setIsCreating] = useState(false);
  const [bulletinBody, setBulletinBody] = useState('');
  const [editingBulletin, setEditingBulletin] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const observerTarget = useRef(null);
  const bulletinsContainerRef = useRef(null);

  // Gallery state for create mode
  const [galleryId, setGalleryId] = useState(null);
  const [localGalleryMedia, setLocalGalleryMedia] = useState([]);
  const [galleryLayout, setGalleryLayout] = useState(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  // Gallery state for edit mode
  const [editGalleryId, setEditGalleryId] = useState(null);
  const [editLocalGalleryMedia, setEditLocalGalleryMedia] = useState([]);
  const [editGalleryLayout, setEditGalleryLayout] = useState(null);
  const [editUploadingGallery, setEditUploadingGallery] = useState(false);

  // Media viewer
  const [viewingMedia, setViewingMedia] = useState(null);

  // Load media layouts
  useEffect(() => {
    if (accessToken) {
      fetchMediaLayouts(accessToken);
    }
  }, [accessToken]);

  // Set context_id when prop changes
  useEffect(() => {
    if (context_id) {
      setContextId(context_id);
    }
  }, [context_id]);

  // Fetch bulletins and calendar data when contextId is set
  useEffect(() => {
    if (contextId) {
      reset();
      fetchBulletins(contextId, 0, 10, accessToken);
      fetchCalendarData(contextId, accessToken);
      setOffset(0);
    }
  }, [contextId, accessToken]);

  // Infinite scroll
  useEffect(() => {
    if (!observerTarget.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && contextId) {
          const newOffset = offset + 10;
          setOffset(newOffset);
          fetchBulletins(contextId, newOffset, 10, accessToken);
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, offset, contextId, accessToken]);

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ date: null, count: 0 });
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dateData = calendarDates.find(d => d.date === dateStr);
      days.push({
        date: day,
        count: dateData ? dateData.count : 0,
        dateStr: dateStr,
        isToday: dateStr === todayStr,
        isSelected: dateStr === selectedDate
      });
    }

    return days;
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2 && contextId) {
      searchBulletins(contextId, searchQuery, accessToken);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    clearSearch();
  };

  const handleSearchResultClick = async (bulletinId) => {
    if (!contextId) return;
    setOffset(0);
    await jumpToBulletin(contextId, bulletinId, accessToken);
  };

  // Extract plain text from HTML
  const stripHTML = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Format date like Posts (relative time)
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  // Disable checkboxes in view mode and restore their checked state
  useEffect(() => {
    if (!bulletinsContainerRef.current) return;

    const checkboxes = bulletinsContainerRef.current.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      // Restore checked state from HTML attribute
      if (checkbox.hasAttribute('checked')) {
        checkbox.checked = true;
      } else {
        checkbox.checked = false;
      }

      // Disable all checkboxes in view mode
      const isInEditMode = checkbox.closest('.rich-text-editor-extended');
      if (!isInEditMode) {
        checkbox.disabled = true;
        checkbox.style.pointerEvents = 'none';
        checkbox.style.cursor = 'default';
      }
    });
  }, [bulletins, searchResults, editingBulletin]);

  // Gallery handlers for CREATE mode
  const handleCreateGallery = async () => {
    if (!profile?.id) {
      alert('Error', 'Profile not found');
      return;
    }

    const result = await createGallery(
      {
        title: 'Bulletin Gallery',
        description: '',
        visibility_id: 3, // PUBLIC - bulletins are public content
      },
      accessToken
    );

    if (result.success) {
      setGalleryId(result.gallery.id);
      setLocalGalleryMedia([]);
      setGalleryLayout(null);
    } else {
      await alert('Error', result.error || 'Failed to create gallery');
    }
  };

  const handleGalleryLayoutChange = async (layoutCode) => {
    if (!galleryId) return;

    setGalleryLayout(layoutCode);

    const result = await updateGallery(galleryId, { layout_code: layoutCode }, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to update gallery layout');
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery) {
        setGalleryLayout(galleryResult.gallery.layout_code);
      }
    }
  };

  const handleAddMediaToGallery = async (files) => {
    if (!galleryId || !files || files.length === 0) return;

    setUploadingGallery(true);

    const result = await addMedia(galleryId, files, accessToken);

    if (!result.success) {
      await alert('Error', result.error || 'Failed to upload media');
    } else {
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }

    setUploadingGallery(false);
  };

  const handleDeleteMediaFromGallery = async (mediaId) => {
    if (!galleryId) return;

    const result = await deleteMedia(galleryId, mediaId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete media');
    } else {
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }
  };

  const handleDeleteGallery = async () => {
    if (!galleryId) return;

    const result = await deleteGallery(galleryId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete gallery');
    } else {
      setGalleryId(null);
      setLocalGalleryMedia([]);
      setGalleryLayout(null);
    }
  };

  const handleReorderMedia = async (mediaOrder) => {
    if (!galleryId) return;

    const result = await reorderMedia(galleryId, mediaOrder, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to reorder media');
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }
  };

  // Gallery handlers for EDIT mode
  const handleEditCreateGallery = async () => {
    if (!profile?.id) {
      alert('Error', 'Profile not found');
      return;
    }

    const result = await createGallery(
      {
        title: 'Bulletin Gallery',
        description: '',
        visibility_id: 3, // PUBLIC - bulletins are public content
      },
      accessToken
    );

    if (result.success) {
      setEditGalleryId(result.gallery.id);
      setEditLocalGalleryMedia([]);
      setEditGalleryLayout(null);
    } else {
      await alert('Error', result.error || 'Failed to create gallery');
    }
  };

  const handleEditGalleryLayoutChange = async (layoutCode) => {
    if (!editGalleryId) return;

    setEditGalleryLayout(layoutCode);

    const result = await updateGallery(editGalleryId, { layout_code: layoutCode }, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to update gallery layout');
      const galleryResult = await fetchGallery(editGalleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery) {
        setEditGalleryLayout(galleryResult.gallery.layout_code);
      }
    }
  };

  const handleEditAddMediaToGallery = async (files) => {
    if (!editGalleryId || !files || files.length === 0) return;

    setEditUploadingGallery(true);

    const result = await addMedia(editGalleryId, files, accessToken);

    if (!result.success) {
      await alert('Error', result.error || 'Failed to upload media');
    } else {
      const galleryResult = await fetchGallery(editGalleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setEditLocalGalleryMedia(galleryResult.gallery.media);
      }
    }

    setEditUploadingGallery(false);
  };

  const handleEditDeleteMediaFromGallery = async (mediaId) => {
    if (!editGalleryId) return;

    const result = await deleteMedia(editGalleryId, mediaId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete media');
    } else {
      const galleryResult = await fetchGallery(editGalleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setEditLocalGalleryMedia(galleryResult.gallery.media);
      }
    }
  };

  const handleEditDeleteGallery = async () => {
    if (!editGalleryId) return;

    const result = await deleteGallery(editGalleryId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete gallery');
    } else {
      setEditGalleryId(null);
      setEditLocalGalleryMedia([]);
      setEditGalleryLayout(null);
    }
  };

  const handleEditReorderMedia = async (mediaOrder) => {
    if (!editGalleryId) return;

    const result = await reorderMedia(editGalleryId, mediaOrder, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to reorder media');
      const galleryResult = await fetchGallery(editGalleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setEditLocalGalleryMedia(galleryResult.gallery.media);
      }
    }
  };

  // Handle view media
  const handleViewMedia = (media) => {
    setViewingMedia(media);
  };

  // Handle calendar date click
  const handleDateClick = async (dateStr) => {
    if (!contextId) return;
    setOffset(0);
    await jumpToDate(contextId, dateStr, accessToken);
  };

  // Handle back to top
  const handleBackToTop = async () => {
    if (!contextId) return;
    setOffset(0);
    setCurrentMonth(new Date()); // Reset calendar to current month
    await resetToLatest(contextId, accessToken);
  };

  // Handle create bulletin
  const handleCreateBulletin = async () => {
    if (!bulletinBody.trim() || !contextId) {
      return;
    }

    const result = await createBulletin(contextId, { body: bulletinBody, gallery_id: galleryId }, accessToken);
    if (result.success) {
      setBulletinBody('');
      setGalleryId(null);
      setLocalGalleryMedia([]);
      setGalleryLayout(null);
      setIsCreating(false);
    } else {
      await alert('Error', result.error || 'Failed to create bulletin');
    }
  };

  const handleCancelCreate = async () => {
    // If gallery was created but bulletin not submitted, delete the gallery
    if (galleryId) {
      await deleteGallery(galleryId, accessToken);
    }
    setIsCreating(false);
    setBulletinBody('');
    setGalleryId(null);
    setLocalGalleryMedia([]);
    setGalleryLayout(null);
  };

  // Handle edit bulletin
  const startEdit = (bulletin) => {
    setEditingBulletin(bulletin);
    setEditBody(bulletin.body);

    // Load existing gallery if bulletin has one
    if (bulletin.gallery) {
      setEditGalleryId(bulletin.gallery.id);
      setEditLocalGalleryMedia(bulletin.gallery.media || []);
      setEditGalleryLayout(bulletin.gallery.layout_code);
    } else {
      setEditGalleryId(null);
      setEditLocalGalleryMedia([]);
      setEditGalleryLayout(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editBody.trim() || !editingBulletin) return;

    const result = await updateBulletin(editingBulletin.uuid, { body: editBody, gallery_id: editGalleryId }, accessToken);
    if (result.success) {
      setEditingBulletin(null);
      setEditBody('');
      setEditGalleryId(null);
      setEditLocalGalleryMedia([]);
      setEditGalleryLayout(null);
    } else {
      await alert('Error', result.error || 'Failed to update bulletin');
    }
  };

  const handleCancelEdit = async () => {
    // If a new gallery was created during edit but not saved, delete it
    if (editGalleryId && (!editingBulletin.gallery || editGalleryId !== editingBulletin.gallery.id)) {
      await deleteGallery(editGalleryId, accessToken);
    }
    setEditingBulletin(null);
    setEditBody('');
    setEditGalleryId(null);
    setEditLocalGalleryMedia([]);
    setEditGalleryLayout(null);
  };

  // Handle delete bulletin
  const handleDelete = async (uuid) => {
    const confirmed = await confirm(
      'Are you sure you want to delete this bulletin? This action cannot be undone.',
      'Delete Bulletin',
      'Delete',
      'Cancel'
    );
    if (!confirmed) return;

    const result = await deleteBulletin(uuid, contextId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete bulletin');
    }
  };

  // Handle toggle pin
  const handleTogglePin = async (bulletin) => {
    await togglePin(bulletin.uuid, !bulletin.is_pinned, contextId, accessToken);
  };

  if (!contextId) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        No bulletin context selected. Please configure the widget.
      </div>
    );
  }

  const displayBulletins = bulletins;
  const calendarDays = generateCalendarDays();

  return (
    <div className="news-bulletin-widget flex flex-col lg:flex-row gap-6">
      {/* Left Sidebar - Calendar and Search */}
      <div className="w-full lg:w-1/4 flex-shrink-0 space-y-6">
        {/* Calendar */}
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              ←
            </button>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </h3>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              →
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-semibold text-gray-600 dark:text-gray-400 pb-1">
                {day}
              </div>
            ))}
            {calendarDays.map((day, idx) => {
              let className = 'p-1 text-center rounded ';

              if (day.date) {
                if (day.isSelected) {
                  // Selected date - bright red
                  className += 'bg-red-600 dark:bg-red-600 text-white font-bold border-2 border-red-800 dark:border-red-400';
                } else if (day.isToday) {
                  // Today - blue outline
                  className += 'border-2 border-blue-500 dark:border-blue-400 font-semibold text-gray-900 dark:text-gray-100';
                  if (day.count > 0) {
                    className += ' bg-red-100 dark:bg-red-900 cursor-pointer hover:bg-red-200 dark:hover:bg-red-800';
                  } else {
                    className += ' text-gray-600 dark:text-gray-400';
                  }
                } else if (day.count > 0) {
                  // Has bulletins - red background
                  className += 'bg-red-100 dark:bg-red-900 cursor-pointer hover:bg-red-200 dark:hover:bg-red-800 font-semibold text-gray-900 dark:text-gray-100';
                } else {
                  // Normal day
                  className += 'text-gray-600 dark:text-gray-400';
                }
              }

              return (
                <div
                  key={idx}
                  className={className}
                  title={day.count > 0 ? `${day.count} bulletin(s)` : ''}
                  onClick={() => day.count > 0 && handleDateClick(day.dateStr)}
                >
                  {day.date || ''}
                </div>
              );
            })}
          </div>
        </div>

        {/* Search */}
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Search</h3>
          <form onSubmit={handleSearch} className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search bulletins..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              />
              <button
                type="submit"
                disabled={searchQuery.trim().length < 2}
                className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
            {searchResults.length > 0 && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
              >
                Clear Search
              </button>
            )}
          </form>

          {/* Search Results List */}
          {searchResults.length > 0 && (
            <div className="mt-4 max-h-96 overflow-y-auto space-y-2">
              {searchResults.map((result) => {
                const plainText = stripHTML(result.body);
                const snippet = plainText.length > 100 ? plainText.substring(0, 100) + '...' : plainText;
                const isSelected = selectedBulletinId === result.id;

                return (
                  <div
                    key={result.id}
                    onClick={() => handleSearchResultClick(result.id)}
                    className={`p-3 rounded cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-red-100 dark:bg-red-900 border-2 border-red-600 dark:border-red-400'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {new Date(result.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {snippet}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Bulletins Timeline */}
      <div className="w-full lg:flex-1 space-y-4">
        {/* Back to Top Button - Show when date or bulletin is selected */}
        {(selectedDate || selectedBulletinId) && (
          <div className="flex justify-center">
            <button
              onClick={handleBackToTop}
              className="w-10 h-10 rounded-full bg-gray-500 hover:bg-gray-600 text-white flex items-center justify-center transition-colors shadow-lg"
              title="Back to Top"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Create Bulletin Button - Admin Only */}
        {isAdmin && !isCreating && (
          <div className="flex justify-end">
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Bulletin
            </button>
          </div>
        )}

        {/* Create Bulletin Form - Admin Only */}
        {isAdmin && isCreating && (
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
            <RichTextEditorExtended
              value={bulletinBody}
              onChange={setBulletinBody}
              placeholder="What's happening?"
            />

            {/* Gallery Section */}
            {galleryId ? (
              <div className="mt-4">
                <GalleryRenderer
                  media={localGalleryMedia}
                  layout={galleryLayout}
                  editMode={true}
                  onLayoutChange={handleGalleryLayoutChange}
                  onAddMedia={handleAddMediaToGallery}
                  onDeleteMedia={handleDeleteMediaFromGallery}
                  onDeleteGallery={handleDeleteGallery}
                  onReorderMedia={handleReorderMedia}
                  availableLayouts={mediaLayouts.filter(l => l.is_active && l.type_id === 2)}
                  uploading={uploadingGallery}
                />
              </div>
            ) : (
              <div className="mt-4">
                <button
                  onClick={handleCreateGallery}
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-400 hover:border-red-600 hover:text-red-600 dark:hover:border-red-400 dark:hover:text-red-400 text-sm"
                >
                  + Add Media Gallery
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={handleCancelCreate}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBulletin}
                disabled={!bulletinBody.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Post
              </button>
            </div>
          </div>
        )}

        {/* Bulletins List */}
        <div ref={bulletinsContainerRef} className="space-y-4">
          {loading && displayBulletins.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
          ) : displayBulletins.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {searchResults.length === 0 && searchQuery ? 'No bulletins found' : 'No bulletins yet'}
            </div>
          ) : (
            displayBulletins.map((bulletin) => (
              <div
                key={bulletin.uuid}
                className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400" title={new Date(bulletin.created_at).toLocaleString()}>
                      {formatDate(bulletin.created_at)}
                      {bulletin.updated_at && bulletin.updated_at !== bulletin.created_at && ' (edited)'}
                    </span>
                  </div>
                  {/* Admin Controls - Admin Only */}
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleTogglePin(bulletin)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-500"
                        title={bulletin.is_pinned ? 'Unpin' : 'Pin'}
                      >
                        {bulletin.is_pinned ? (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="8" />
                            <circle cx="12" cy="12" r="2" fill="white" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"/>
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(bulletin)}
                        className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(bulletin.uuid)}
                        className="text-red-600 dark:text-red-400 hover:underline text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                {editingBulletin?.uuid === bulletin.uuid ? (
                  <div>
                    <RichTextEditorExtended
                      value={editBody}
                      onChange={setEditBody}
                      placeholder="Edit bulletin..."
                    />

                    {/* Edit Mode Gallery Section */}
                    {editGalleryId ? (
                      <div className="mt-4">
                        <GalleryRenderer
                          media={editLocalGalleryMedia}
                          layout={editGalleryLayout}
                          editMode={true}
                          onLayoutChange={handleEditGalleryLayoutChange}
                          onAddMedia={handleEditAddMediaToGallery}
                          onDeleteMedia={handleEditDeleteMediaFromGallery}
                          onDeleteGallery={handleEditDeleteGallery}
                          onReorderMedia={handleEditReorderMedia}
                          availableLayouts={mediaLayouts.filter(l => l.is_active && l.type_id === 2)}
                          uploading={editUploadingGallery}
                        />
                      </div>
                    ) : (
                      <div className="mt-4">
                        <button
                          onClick={handleEditCreateGallery}
                          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-400 hover:border-red-600 hover:text-red-600 dark:hover:border-red-400 dark:hover:text-red-400 text-sm"
                        >
                          + Add Media Gallery
                        </button>
                      </div>
                    )}

                    <div className="flex gap-2 mt-4 justify-end">
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className="text-gray-800 dark:text-gray-200 mb-3"
                      dangerouslySetInnerHTML={{ __html: bulletin.body }}
                    />

                    {/* Display Gallery in View Mode */}
                    {bulletin.gallery && bulletin.gallery.media && bulletin.gallery.media.length > 0 && (
                      <div className="mt-4">
                        <GalleryRenderer
                          media={bulletin.gallery.media}
                          layout={bulletin.gallery.layout_code}
                          editMode={false}
                          onViewMedia={handleViewMedia}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          )}

          {hasMore && <div ref={observerTarget} className="h-4" />}
          {loading && displayBulletins.length > 0 && (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading more...</div>
          )}
        </div>
      </div>

      {/* Custom styles for rich text content */}
      <style>{`
        .news-bulletin-widget h1 {
          font-size: 2em;
          font-weight: 700;
          margin: 0.67em 0;
        }
        .news-bulletin-widget h2 {
          font-size: 1.5em;
          font-weight: 600;
          margin: 0.75em 0;
        }
        .news-bulletin-widget h3 {
          font-size: 1.17em;
          font-weight: 600;
          margin: 0.83em 0;
        }
        .news-bulletin-widget h4 {
          font-size: 1em;
          font-weight: 600;
          margin: 1em 0;
        }
        .news-bulletin-widget ol {
          list-style-type: decimal;
          padding-left: 2em;
          margin: 1em 0;
        }
        .news-bulletin-widget ul {
          list-style-type: disc;
          padding-left: 2em;
          margin: 1em 0;
        }
        .news-bulletin-widget ol li,
        .news-bulletin-widget ul li {
          margin: 0.5em 0;
        }
        .todo-list {
          list-style: none;
          padding-left: 0;
        }
        .todo-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.5em;
          margin: 0.25em 0;
        }
        .todo-list input[type="checkbox"] {
          appearance: none;
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          margin-top: 0.25em;
          cursor: pointer;
          border: 2px solid #6b7280;
          border-radius: 3px;
          background: transparent;
          position: relative;
          flex-shrink: 0;
        }
        .todo-list input[type="checkbox"]:checked {
          background: transparent;
          border-color: #6b7280;
        }
        .todo-list input[type="checkbox"]:checked::after {
          content: '✓';
          position: absolute;
          top: -3px;
          left: 1px;
          color: #dc2626;
          font-size: 20px;
          font-weight: 900;
          line-height: 1;
        }
        .todo-list input[type="checkbox"]:disabled {
          cursor: default;
          opacity: 1;
        }
      `}</style>

      {/* View Media Modal */}
      {viewingMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={() => setViewingMedia(null)}
        >
          <button
            onClick={() => setViewingMedia(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="max-w-6xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            {viewingMedia.kind_code === 'IMAGE' ? (
              <ProtectedImage
                uuid={viewingMedia.uuid}
                alt={viewingMedia.storage_key}
                className="max-w-full max-h-[90vh] object-contain"
              />
            ) : (
              <ProtectedVideo
                uuid={viewingMedia.uuid}
                controls={true}
                autoPlay={true}
                className="max-w-full max-h-[90vh]"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsBulletinWidget;
